import React, { useCallback, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence } from 'framer-motion';
import { ThemeContext } from 'styled-components';

import { GRID_GUTTER_DESKTOP, GRID_GUTTER_LAPTOP, GRID_GUTTER_MOBILE } from '../config';
import YatDraggableMasonry from './DraggableMasonry';
import { EmptyPage } from './EmptyPage';
import LinkItemEditMobile from './ItemEditMobile';
import { GridWrapper, LoadMoreWrapper } from './styles';
import { moveCardPosition, setDraggingItem } from '@/store/actions/yat-page-item-actions';
import { getYatPageItems } from '@/store/actions/yat-page-items';
import { setYatPageAssets } from '@/store/slices';

import { useMediaQuery } from '@/providers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DragToScrollCallback } from '@/hooks/useDragScroll';

import { Button } from '@/components/Sections/Profile/Buttons';

interface Props {
    editMode: boolean;
    onDragToScroll: DragToScrollCallback;
    isCurrentUserProfile?: boolean;
}

const YatGrid: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
    { editMode, onDragToScroll, isCurrentUserProfile }: Props,
    ref,
) => {
    const { desktop, ultrawide } = useMediaQuery();
    const { ref: loadMoreRef, inView } = useInView();
    const theme = useContext(ThemeContext);
    const pageId = useAppSelector((state) => state.yatPage.id);
    const eid = useAppSelector((state) => state.yatPage.eid);
    const paging = useAppSelector((state) => state.yatPage.paging);
    const pageAssets = useAppSelector((state) => state.yatPage.pageAssets);
    const pageAssetsSorted = useAppSelector((state) => state.yatPage.pageAssets.sorted);
    const isLoadingItems = useAppSelector((state) => state.yatPage.isLoadingItems);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);

    const dispatch = useAppDispatch();
    const columns = desktop ? 3 : 2;

    const handleDragStart = (draggingId: string) => {
        dispatch(setDraggingItem(draggingId));
    };

    const handleDrag = (underId: string) => {
        dispatch(moveCardPosition(underId));
    };

    const handleDragEnd = () => {
        dispatch(setDraggingItem());
    };

    const hasNext = paging?.total && paging?.total > pageAssetsSorted?.length;

    const handleFetchNext = useCallback(() => {
        const nextPage = (paging?.page || 0) + 1;
        if (eid && pageId) dispatch(getYatPageItems(eid, pageId, true, nextPage.toString()));
    }, [paging, pageId, eid, dispatch]);

    const handleCleanAssets = useCallback(() => {
        dispatch(setYatPageAssets([]));
    }, [dispatch]);

    useEffect(() => {
        if (inView && !isLoadingItems) {
            handleFetchNext();
        }
    }, [inView, isLoadingItems, handleFetchNext]);

    useEffect(() => handleCleanAssets, [handleCleanAssets]);

    return (
        <AnimatePresence>
            {!editingLiquidID && !liquidIDExpandedPreview && (
                <GridWrapper
                    ref={ref}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        duration: theme.transitions.speeds.normal,
                        ease: theme.transitions.eases.cubic,
                        delay: pageAssetsSorted?.length ? 0 : 1.2,
                    }}
                >
                    {!pageAssetsSorted?.length && !isLoadingItems ? (
                        <EmptyPage isCurrentUserProfile={isCurrentUserProfile} />
                    ) : (
                        <YatDraggableMasonry
                            columns={columns}
                            columnGutter={
                                ultrawide ? GRID_GUTTER_DESKTOP : desktop ? GRID_GUTTER_LAPTOP : GRID_GUTTER_MOBILE
                            }
                            onDragStart={handleDragStart}
                            onDrag={handleDrag}
                            onDragEnd={handleDragEnd}
                            editMode={editMode}
                            isLoading={isLoadingItems}
                            onDragToScroll={onDragToScroll}
                            assets={pageAssets}
                        />
                    )}

                    <LinkItemEditMobile editMode={editMode} />
                    {!!hasNext && !isLoadingItems && !!pageAssetsSorted?.length && (
                        <LoadMoreWrapper>
                            <Button onClick={handleFetchNext} ref={loadMoreRef}>
                                Load more
                            </Button>
                        </LoadMoreWrapper>
                    )}
                </GridWrapper>
            )}
        </AnimatePresence>
    );
};

export default React.forwardRef(YatGrid);
